import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

const HeaderContainer = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.$scrolled 
    ? 'rgba(255, 248, 245, 0.95)' 
    : 'rgba(255, 248, 245, 0.8)'};
  backdrop-filter: blur(16px);
  border-bottom: 1px solid ${props => props.$scrolled 
    ? theme.colors.border.medium 
    : 'transparent'};
  box-shadow: ${props => props.$scrolled 
    ? theme.shadows.medium 
    : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: ${props => props.$scrolled ? '12px 0' : '20px 0'};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.lg};
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoIcon = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${theme.spacing.md};
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: rotate(5deg) scale(1.05);
  }
  
  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
    display: block;
    max-width: 100%;
    max-height: 100%;
    position: relative;
    z-index: 2;
    border-radius: 8px;
    /* 如果图标太淡，可以添加对比度或阴影 */
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
    /* 如果需要白色图标，取消注释下面这行 */
    /* filter: brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0,0,0,0.3)); */
  }
  
  /* 备用图标 - 文字Logo - 只在图片加载失败时显示 */
  &::before {
    content: '厨';
    font-size: 28px;
    font-weight: bold;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    z-index: 1;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

const LogoTitle = styled.h1`
  font-size: ${theme.typography.fontSize.xxl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
  font-family: ${theme.typography.fontFamily.secondary};
  letter-spacing: -0.5px;
`;

const LogoSubtitle = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-top: -2px;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const NavLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily.primary};
  cursor: pointer;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.small};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.surface};
    transform: translateY(-1px);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CTAButton = styled.button`
  background: ${theme.gradients.primary};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.large};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${theme.shadows.medium};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: ${theme.shadows.large};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.01);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 24px;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.surface};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // 尝试预加载图片
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      console.error('Failed to preload icon');
      setImageLoaded(false);
    };
    img.src = `${process.env.PUBLIC_URL}/icon.png`;
  }, []);

  const handleContact = () => {
    window.location.href = 'mailto:blackenvelope@163.com?subject=联系我们&body=您好，我想了解更多关于厨记的信息。';
  };

  return (
    <HeaderContainer $scrolled={scrolled}>
      <Nav>
        <LogoContainer>
          <LogoIcon>
            {imageLoaded && (
              <img 
                src={`${process.env.PUBLIC_URL}/icon.png`}
                alt="厨记" 
                onError={(e) => {
                  console.error('Icon failed to load:', e);
                  setImageLoaded(false);
                }}
                onLoad={(e) => {
                  console.log('Icon loaded successfully');
                  const img = e.currentTarget as HTMLImageElement;
                  console.log('Image dimensions:', img.naturalWidth, 'x', img.naturalHeight);
                  setImageLoaded(true);
                }}
                style={{ position: 'relative', zIndex: 2 }}
              />
            )}
          </LogoIcon>
          <LogoText>
            <LogoTitle>厨记</LogoTitle>
            <LogoSubtitle>CHU JI</LogoSubtitle>
          </LogoText>
        </LogoContainer>
        
        <NavActions>
          <MobileMenuButton>
            ☰
          </MobileMenuButton>
        </NavActions>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
